import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useGetAuthData } from 'src/hooks';
import { Actions as FilterActions } from 'src/store/requests/filter/actions';
import { IMessage, CRMNames } from 'src/typings/app';
import { Actions } from 'src/store/app/actions';
import { Actions as ActionsCross } from 'src/store/crosstable/actions';
import { Actions as ActionsFloors } from 'src/store/floors/actions';
import {
  getAmoToken,
  getComplexByAmoPipeline,
  getBitrixDeal,
  getPlacementByBitrix,
  getPlacementByAmo,
  getComplexData,
  getCreatioDeal,
  getUserData,
  getPlacementByCreatio,
  getPlacementByNetHunt,
  getNetHuntDeal, getComplexDataByGetParams,
} from 'src/api';
import { pathList } from 'src/route-list';
import { IComplex } from 'src/typings/complexes';
import { IUsers } from 'src/typings/user';
import { useQueryClient } from '@tanstack/react-query';
import { toQueryString } from 'src/helpers/buildQueryParamsString';
import { complexRequestProperties } from 'src/components/profileComponents/Complexes/types';

export const useSubsribeMessage = () => {
  const { setJWT } = useGetAuthData();
  const history = useHistory();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  // CRMs re-deliver the boot postMessage; re-running the whole boot chain
  // (user/complexes/deal fetches + history.push) for the same identity is
  // wasteful and can fight the user's navigation. Keyed dedup, survives
  // re-renders (the listener is re-registered on every render).
  const processedBootKey = useRef<string | null>(null);
  const handlerAmo = async (data: IMessage, cleanupFunction: boolean) => {
    const {
      userEmail,
      pipelineId,
      dealId,
    } = data;
    const formatId = `${pipelineId}.${dealId}`;
    if (dealId === '0') {
      history.push(`${pathList.errorPage}/zeroDeal`);
    } else {
      dispatch(Actions.setCrmData(data));
      const res = await getAmoToken(userEmail);
      if (!cleanupFunction) {
        setJWT(res.data.token);
        if (pipelineId) {
          const { data: placements } = await getPlacementByAmo(res.data.token, formatId);
          if (placements['hydra:totalItems']) {
            dispatch(Actions.selectAppartment(placements['hydra:member'][0].id));
            dispatch(Actions.setIsOpenAppartSidebar(true));
          }
          const comp = await getComplexByAmoPipeline(res.data.token, pipelineId);
          if (comp.data['hydra:totalItems'] && comp.data['hydra:member'][0]) {
            const compId = comp.data['hydra:member'][0].id;
            history.push(`/${compId}/`);
          } else {
            history.push(`${pathList.errorPage}/unknownPipeline`);
          }
        }
      }
    }
  };

  const handlerBitrix = async (data: IMessage, cleanupFunction: boolean) => {
    const {
      userEmail,
      dealId,
      domain,
      categoryId,
    } = data;

    const { token } = data;
    if (!cleanupFunction && domain) {
      // Hardening: `domain` is interpolated into request params/links — accept
      // only a bare hostname (blocks path/scheme smuggling); a token, when
      // present, must be a string.
      if (typeof domain !== 'string' || !/^[a-z0-9][a-z0-9.-]{1,253}$/i.test(domain)) return;
      if (token && typeof token !== 'string') return;
      // Apply the (possibly rotated) JWT BEFORE the dedup check, so a new token
      // under the same boot identity still lands (setJWT is idempotent).
      // Guarded: setJWT(undefined) used to persist the literal string "undefined".
      // A missing token must NOT abort the boot — setCrmData below releases the
      // route boot gate, and the flow degrades exactly like branch A did.
      if (token) {
        setJWT(token);
      }
      const bootKey = `bitrix:${domain}:${dealId}:${userEmail}`;
      if (processedBootKey.current === bootKey) return;
      processedBootKey.current = bootKey;
      dispatch(Actions.setCrmData(data));
      if (dealId) {
        // Entered with a deal → the module (crosstable) is very likely the target.
        // Hold a preloader so the complexes list doesn't flash before the deal's
        // placement is resolved and we navigate to its complex. Reset either on
        // SET_COMPLEX_ID (module opened) or on the no-binding branches below.
        dispatch(Actions.setWidgetBootPending(true));
        // Safety net: never leave the preloader stuck if a branch is missed.
        setTimeout(() => dispatch(Actions.setWidgetBootPending(false)), 8000);
      }
      const { data: complexes } = await getComplexDataByGetParams(token, toQueryString({
        ...complexRequestProperties,
        'users.email': userEmail,
        bitrixCategories: categoryId,
        'developers.bitrixProject.domain': domain,
      }));
      if (complexes['hydra:totalItems']) {
        dispatch(ActionsCross.setAvailableComplexes(complexes['hydra:member']));
      }
      if (dealId) {
        // The deal payload carries @accessRules/virtual blocks; never seed the
        // store with undefined when the fetch fails.
        const { data: bitrixDeal } = await getBitrixDeal(token, dealId);
        if (bitrixDeal) {
          dispatch(ActionsCross.setBitrixDeal(bitrixDeal));
        }
        const { data: placements } = await getPlacementByBitrix(token, dealId);
        dispatch(Actions.setBindingAppartment(!!placements['hydra:totalItems']));
        if (!placements['hydra:totalItems']) {
          // No binding → the complexes list is the correct landing; drop the preloader.
          dispatch(Actions.setWidgetBootPending(false));
        }
        if (placements['hydra:totalItems']) {
          dispatch(Actions.selectAppartment(placements['hydra:member'][0].id));
          dispatch(Actions.setIsOpenAppartSidebar(true));
          const compAccess = complexes['hydra:member'].find((comp: IComplex) => {
            return comp.id === placements['hydra:member'][0].complex.id;
          });
          if (compAccess) {
            const firstComplexId = placements['hydra:member'][0].complex.id;
            const response = await getComplexData(token, firstComplexId);

            queryClient.setQueryData(
              ['complex', String(firstComplexId)],
              response,
            );

            const { data: firstComplex } = response;
            if (firstComplex && firstComplex.crosstableLanguage) {
              dispatch(ActionsCross.setDefaultPlacementType(placements['hydra:member'][0].placementType.title[firstComplex.crosstableLanguage]));
              dispatch(ActionsFloors.setSelectedFloor(String(placements['hydra:member'][0].floors[0].id)));
              dispatch(ActionsFloors.setSelectedFloorNumber(placements['hydra:member'][0].floors[0].numeration[firstComplex.crosstableLanguage]));
              // Set complexId SYNCHRONOUSLY (not just via the URL). complexId is
              // otherwise only set by ComplexIdWrapper, which lives INSIDE the
              // <Switch> — but the boot preloader guard in route.tsx returns the
              // loader before the <Switch> whenever complexId is null, so
              // ComplexIdWrapper never mounts and complexId is never set: a deadlock
              // that would only release on the 8s safety timeout. Setting it here
              // flips widgetBootPending off (SET_COMPLEX_ID reducer), the guard
              // passes, and ComplexIdWrapper then re-confirms the same id (idempotent).
              dispatch(Actions.setComplexId(String(firstComplexId)));
              history.push(`/${firstComplexId}/`);
            } else {
              // Complex fetched but no crosstable language configured — resolve the
              // preloader instead of leaving it to the safety timeout.
              dispatch(Actions.setWidgetBootPending(false));
            }
          } else {
            dispatch(Actions.setWidgetBootPending(false));
            history.push(`${pathList.errorPage}/wrongComplexAccess`);
          }
        }
      }
    }
  };

  const handlerCreatio = async (data: IMessage, cleanupFunction: boolean) => {
    const {
      dealId,
      domain,
      projectId,
      token,
    } = data;
    if (!cleanupFunction && domain && token) {
      dispatch(Actions.setCrmData(data));
      setJWT(token);
      const { data: user }: {
        data: IUsers
      } = await getUserData(token);
      const { data: creatioDeal } = await getCreatioDeal(token, dealId);
      dispatch(ActionsCross.setCreatioDeal({
        ...creatioDeal,
        id: dealId,
      }));
      const { data: complexes } = await getComplexDataByGetParams(token, toQueryString({
        ...complexRequestProperties,
        'users.email': user.email,
        'developers.creatioProject': projectId,
      }));

      if (complexes['hydra:totalItems']) {
        dispatch(ActionsCross.setAvailableComplexes(complexes['hydra:member']));
      }
      const { data: placements } = await getPlacementByCreatio(token, dealId);
      dispatch(Actions.setBindingAppartment(!!placements['hydra:totalItems']));
      if (placements['hydra:totalItems']) {
        dispatch(Actions.selectAppartment(placements['hydra:member'][0].id));
        dispatch(Actions.setIsOpenAppartSidebar(true));
        const compAccess = complexes['hydra:member'].find((comp: IComplex) => {
          return comp.id === placements['hydra:member'][0].complex.id;
        });
        if (compAccess) {
          const firstComplexId = placements['hydra:member'][0].complex.id;
          const response = await getComplexData(token, firstComplexId);

          queryClient.setQueryData(
            ['complex', String(firstComplexId)],
            response,
          );

          const { data: firstComplex } = response;
          if (firstComplex && firstComplex.language) {
            dispatch(ActionsCross.setDefaultPlacementType(placements['hydra:member'][0].placementType.title[firstComplex.language]));
            dispatch(ActionsFloors.setSelectedFloor(String(placements['hydra:member'][0].floors[0].id)));
            dispatch(ActionsFloors.setSelectedFloorNumber(placements['hydra:member'][0].floors[0].numeration[firstComplex.language]));
            history.push(`/${firstComplexId}/`);
          }
        } else {
          history.push(`${pathList.errorPage}/wrongComplexAccess`);
        }
      }
    }
  };

  const handlerNetHunt = async (data: IMessage, cleanupFunction: boolean) => {
    const {
      dealId,
      projectId,
      token,
    } = data;
    if (!cleanupFunction && token) {
      dispatch(Actions.setCrmData(data));
      setJWT(token);
      const { data: user }: {
        data: IUsers
      } = await getUserData(token);
      const { data: netHuntDeal } = await getNetHuntDeal(token, dealId);
      dispatch(ActionsCross.setNetHuntDeal({
        ...netHuntDeal,
        id: dealId,
      }));
      const { data: complexes } = await getComplexDataByGetParams(token, toQueryString({
        ...complexRequestProperties,
        'users.email': user.email,
        'developers.netHuntIntegration': projectId,
      }));

      if (complexes['hydra:totalItems']) {
        dispatch(ActionsCross.setAvailableComplexes(complexes['hydra:member']));
      }
      const { data: placements } = await getPlacementByNetHunt(token, dealId);
      dispatch(Actions.setBindingAppartment(!!placements['hydra:totalItems']));
      if (placements['hydra:totalItems']) {
        dispatch(Actions.selectAppartment(placements['hydra:member'][0].id));
        dispatch(Actions.setIsOpenAppartSidebar(true));
        const compAccess = complexes['hydra:member'].find((comp: IComplex) => {
          return comp.id === placements['hydra:member'][0].complex.id;
        });
        if (compAccess) {
          const firstComplexId = placements['hydra:member'][0].complex.id;
          const response = await getComplexData(token, firstComplexId);

          queryClient.setQueryData(
            ['complex', String(firstComplexId)],
            response,
          );

          const { data: firstComplex } = response;
          if (firstComplex && firstComplex.language) {
            dispatch(ActionsCross.setDefaultPlacementType(placements['hydra:member'][0].placementType.title[firstComplex.language]));
            dispatch(ActionsFloors.setSelectedFloor(String(placements['hydra:member'][0].floors[0].id)));
            dispatch(ActionsFloors.setSelectedFloorNumber(placements['hydra:member'][0].floors[0].numeration[firstComplex.language]));
            history.push(`/${firstComplexId}/`);
          }
        } else {
          history.push(`${pathList.errorPage}/wrongComplexAccess`);
        }
      }
    }
  };

  useEffect(() => {
    let cleanupFunction = false;
    const listener = (e: any) => {
      // Any third-party script can post messages; a non-object payload used to
      // throw right here on e.data.type.
      if (!e?.data || typeof e.data !== 'object') return;
      // CRM boots are side-effectful (JWT, fetch chain, navigation) — accept
      // them only from the embedding wrapper (parent) or self. update2d stays
      // unrestricted: it is a harmless refresh and may come from inner frames.
      const fromParentOrSelf = e.source === window.parent || e.source === window;
      if (e.data.type === 'amocrm' && fromParentOrSelf) {
        handlerAmo({
          crm: CRMNames.amocrm,
          ...e.data.data,
        }, cleanupFunction);
      }
      if (e.data.type === 'bitrix24' && fromParentOrSelf) {
        handlerBitrix({
          crm: CRMNames.bitrix,
          ...e.data.data,
        }, cleanupFunction);
      }
      if (e.data.type === 'creatio' && fromParentOrSelf) {
        handlerCreatio({
          crm: CRMNames.creatio,
          ...e.data.data,
        }, cleanupFunction);
      }
      if (e.data.type === 'netHunt' && fromParentOrSelf) {
        handlerNetHunt({
          crm: CRMNames.netHunt,
          ...e.data.data,
        }, cleanupFunction);
      }
      if (e.data.type === 'update2d') {
        dispatch(FilterActions.getDataFilterUpdate());
      }
    };
    window.addEventListener('message', listener, false);

    return () => {
      cleanupFunction = true;
      window.removeEventListener('message', listener, false);
    };
  });
};
